import { useState } from "react";
import { styled } from "@pigment-css/react";
import { Flex } from "../../../primitives";
import { MetricFigures } from "../../ui";
import { useTranslations, useLocale } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { KpiComparisonMode, StandardPeriod } from "@acto/taskpane/core/domain";
import { BarChart } from "./BarChart";
import { useCustomerKpiFigures } from "@acto/taskpane/core/data-access/customers/useCustomerKpiFigures";
import { formatComparisonRange } from "../../../../utils/dateFormatters";
import {
  type CustomerMetric,
  METRIC_KPI_TYPE,
  formatMetricWithoutRatio,
  formatMetricValue,
  useMetricLabels,
} from "../customerMetric";

interface CustomerOverviewSectionProps {
  clientId: string;
  period?: StandardPeriod;
  dateRange?: { startDate: string; endDate: string };
  showTrends?: boolean;
  metric: CustomerMetric;
}

const LoadingSkeleton = styled("div")(({ theme }) => ({
  width: "100%",
  height: 300,
  backgroundColor: theme.managementView.colors.neutral[4],
  borderRadius: theme.managementView.radius[16],
  animation: "pulse 1.5s ease-in-out infinite",
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.5 },
  },
}));

const EmptyState = styled("div")(({ theme }) => ({
  textAlign: "center",
  padding: theme.managementView.spacing[32],
  color: theme.managementView.colors.neutral[56],
  fontSize: theme.managementView.font.size[14],
}));

export const CustomerOverviewSection = ({
  clientId,
  period = StandardPeriod.LAST_12_MONTHS,
  dateRange,
  showTrends = true,
  metric,
}: CustomerOverviewSectionProps) => {
  const t = useTranslations();
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const comparisonMode = useComparisonModeContext();
  const metricLabels = useMetricLabels();
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  const { byKpi, isPending, isFetching } = useCustomerKpiFigures({ clientId, period, dateRange, comparisonMode });
  const chartData = byKpi[METRIC_KPI_TYPE[metric]];
  const revenueData = byKpi.REVENUE;

  const handleMonthSelect = (monthIndex: number | null) => {
    setSelectedMonth(monthIndex === selectedMonth ? null : monthIndex);
  };

  const isLoadingMetric = isPending || isFetching;
  const metricLabel = metricLabels[metric].label;
  const comparisonLabel =
    comparisonMode === KpiComparisonMode.PERIOD_OVER_PERIOD ? t("kpi.previousPeriod") : t("kpi.previousYear");
  const previousTotal = chartData?.previousTotalValue ?? null;
  const previousRange = chartData?.previousRange ?? null;

  return (
    <Flex direction="column" gap={32}>
      {isLoadingMetric ? (
        <LoadingSkeleton />
      ) : !chartData || chartData.values.length === 0 ? (
        <EmptyState>{t("managementView.customerDetails.noDataAvailable")}</EmptyState>
      ) : (
        <>
          <MetricFigures
            label={dateRange ? `${metricLabel} (${formatComparisonRange(dateRange, locale)})` : metricLabel}
            value={formatMetricValue(metric, chartData.totalValue, revenueData?.totalValue ?? null, locale, currency)}
            comparison={
              showTrends && previousTotal !== null && previousRange
                ? {
                    label: `${comparisonLabel} (${formatComparisonRange(previousRange, locale)})`,
                    value: formatMetricValue(
                      metric,
                      previousTotal,
                      revenueData?.previousTotalValue ?? null,
                      locale,
                      currency,
                    ),
                    percentageChange: chartData.percentageChange,
                  }
                : undefined
            }
          />

          <BarChart
            labels={chartData.labels}
            values={chartData.values}
            selectedMonth={selectedMonth}
            onMonthSelect={handleMonthSelect}
            formatValue={(value) => formatMetricWithoutRatio(metric, value, locale, currency)}
            integerYTicks={metric === "orders"}
          />
        </>
      )}
    </Flex>
  );
};
